import {Router, Redirect, Route, Switch} from 'react-router-dom';
import Header from "./components/Header";
import Footer from "./components/Footer";
import Loading from "./components/Loading";
import 'reset-css';
import './sass/base/base.scss';
import './sass/base/layout.scss';
import './sass/app.scss';
import ProductListPage from "./components/ProductListPage";
import Basket from "./components/Basket";
import Login from "./components/Login";
import ProdItem from "./components/ProdItem";
import ProdDetails from "./components/ProdDetails";
import ProductSearch from "./components/ProductSearch";
import Account from "./components/Account";
import Orders from "./components/Orders";
import Checkout from "./components/Checkout";
import Privacy from "./components/Privacy";
import TermsConditions from "./components/TermsConditions";
import Contact from "./components/Contact";
import Thankyou from "./components/Thankyou";
import Profile from "./components/Profile";
import OrderDetails from "./components/OrderDetails";
import TastingNotes from "./components/TastingNotes";
import CustomerAccount from "./components/CustomerAccount";
import {useAuth0} from "@auth0/auth0-react";
import history from "./utils/history";
import {ReactSession} from "react-client-session";
import * as Modules from "./components/Modules";
import React, {useEffect, useState} from "react";

import { SigninContext } from './contexts/SigninContext'
import ProcessingOrder from "./components/ProcessingOrder";
import PaymentConfirmed from "./components/PaymentConfirmed";
import PaymentConfirmedAnon from "./components/PaymentConfirmedAnon";
import AdminSettings from './components/AdminSettings';
import OrderForCustomer from './components/OrderForCustomer';
import Banner from './components/Banner';
import { useAppSettings } from './hooks/useAppSettings';

function App() {
    const {isLoading, isAuthenticated} = useAuth0();
    const settings = useAppSettings();
           const isAdmin = settings && settings['__is_admin_user'] === true;
    // Initialize userName and customerName from sessionStorage if they exist
    const [userName, setUserName] = React.useState(() => {
        const savedUserName = sessionStorage.getItem('selectedUserName');
        return savedUserName ? savedUserName : false;
    });

    const [customerName, setCustomerName] = React.useState(() => {
        const savedCustomerName = sessionStorage.getItem('customerName');
        return savedCustomerName || '';
    });

    // Store the original logged-in user account
    const [originalUserName, setOriginalUserName] = React.useState(() => {
        const savedOriginalUserName = sessionStorage.getItem('originalUserName');
        return savedOriginalUserName || null;
    });

    const [originalCustomerName, setOriginalCustomerName] = React.useState(() => {
        const savedOriginalCustomerName = sessionStorage.getItem('originalCustomerName');
        return savedOriginalCustomerName || '';
    });

    const [displayProfile, setDisplayProfile] = React.useState(false)

    // Custom setUserName wrapper that also saves to sessionStorage
    const setUserNameWithStorage = React.useCallback((newUserName) => {
      setUserName(newUserName);
      if (newUserName) {
        sessionStorage.setItem('selectedUserName', newUserName);
        // Set original user on first login if not already set OR if previous userName was false (fresh login)
        if (!originalUserName || userName === false) {
          setOriginalUserName(newUserName);
          sessionStorage.setItem('originalUserName', newUserName);
        }
      } else {
        sessionStorage.removeItem('selectedUserName');
      }
    }, [originalUserName, userName]);

    // Custom setCustomerName wrapper that also saves to sessionStorage
    const setCustomerNameWithStorage = React.useCallback((newCustomerName) => {
      setCustomerName(newCustomerName);
      if (newCustomerName) {
        sessionStorage.setItem('customerName', newCustomerName);
        // Set original customer name on first login if not already set OR if previous userName was false (fresh login)
        if (!originalCustomerName || userName === false) {
          setOriginalCustomerName(newCustomerName);
          sessionStorage.setItem('originalCustomerName', newCustomerName);
        }
      } else {
        sessionStorage.removeItem('customerName');
      }
    }, [originalCustomerName, userName]);

    // Function to switch back to original user
    const switchToOriginalUser = React.useCallback(() => {
      if (originalUserName) {
        setUserNameWithStorage(originalUserName);
        if (originalCustomerName) {
          setCustomerNameWithStorage(originalCustomerName);
        }
      }
    }, [originalUserName, originalCustomerName, setUserNameWithStorage, setCustomerNameWithStorage]);

    // Clear sessionStorage when user is not authenticated (logged out)
    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        sessionStorage.removeItem('selectedUserName');
        sessionStorage.removeItem('customerName');
        sessionStorage.removeItem('originalUserName');
        sessionStorage.removeItem('originalCustomerName');
        sessionStorage.removeItem('allTradeCustomers');
        setUserName(false);
        setCustomerName('');
        setOriginalUserName(null);
        setOriginalCustomerName('');
      }
    }, [isAuthenticated]);

    // loading status from auth0 api
    if (isLoading) {
        return <Loading/>;
    } else {
        if (isAuthenticated) {
            ReactSession.setStoreType("localStorage");

            return (
                <Router history={history}>
                    <div className="App">
                        <SigninContext.Provider
                            value={{
                                userName,
                                customerName,
                                setUserName: setUserNameWithStorage,
                                setCustomerName: setCustomerNameWithStorage,
                                setDisplayProfile,
                                originalUserName,
                                originalCustomerName,
                                switchToOriginalUser
                            }}
                        >
                            {displayProfile ? <Profile /> : <Profile />}

                        <Header/>
                        <div className="content container">
                            <Switch>
                                <Route path="/product-list/:pageParam/:pageSizeParam">
                                    <Banner/>
                                    <ProductListPage/>
                                </Route>
                                <Route exact path="/product-search/:pageParam/:pageSizeParam">
                                    <ProductSearch/>
                                </Route>
                                <Route exact path="/account">
                                    <Account/>
                                </Route>
                                <Route exact path="/profile">
                                    <Profile/>
                                </Route>
                                <Route exact path="/orders">
                                    <Orders/>
                                </Route>
                                <Route exact path="/order-details/:orderNo">
                                    <OrderDetails/>
                                </Route>
                                <Route exact path="/basket">
                                    <Basket/>
                                </Route>
                                <Route exact path="/checkout">
                                    <Checkout/>
                                </Route>
                                <Route exact path="/login">
                                    <Login/>
                                </Route>
                                <Route path="/product-detail/:id/">
                                    <ProdDetails/>
                                </Route>
                                <Route path="/tasting-notes">
                                    <TastingNotes/>
                                </Route>
                                <Route path="/privacy">
                                    <Privacy/>
                                </Route>
                                <Route path="/terms-conditions">
                                    <TermsConditions/>
                                </Route>
                                <Route path="/contact">
                                    <Contact/>
                                </Route>
                                <Route path="/thank-you/:orderId/:backOrderId/:reservesOrderId">
                                    <ProcessingOrder/>
                                </Route>
                                <Route path="/payment-confirmed/:sessionId/">
                                    <PaymentConfirmed/>
                                </Route>
                                <Route path="/send">
                                </Route>
                                <Route path="/admin/settings">
                                    <AdminSettings/>
                                </Route>
                                <Route path="/order-for-customer">
                                    <OrderForCustomer/>
                                </Route>
                                <Redirect from='/product-list' to='/product-list/1/20' exact/>
                                <Redirect from='/product-search' to='/product-search/1/20' exact/>
                                <Redirect to={isAuthenticated ? '/product-list' : '/login'}/>
                            </Switch>
                        </div>
                        <Footer/>
                        </SigninContext.Provider>
                    </div>
                </Router>
            );
        } else {
            return (
                <Router history={history}>
                    <div className="App">
                        <Header/>
                        <div className="content container">
                            <Switch>
                                <Route exact path="/trade-account">
                                    {process.env.REACT_APP_PROSPECTS === "1" &&
                                        <Modules.Fallback fallback={<Redirect to='/login'/>}>
                                            <Modules.Prospects/>
                                        </Modules.Fallback>
                                    }
                                </Route>
                                <Route path="/login">
                                    <Login/>
                                </Route>
                                <Route path="/payment-confirmed/:sessionId/">
                                    <PaymentConfirmedAnon/>
                                </Route>
                                <Redirect to='/login'/>
                            </Switch>
                        </div>
                    </div>
                </Router>
            )
        }
    }
}

export default App;
